import { inject, Injectable, computed } from '@angular/core';
import {
  CreateWorkflowSuccessResponse,
  DeleteWorkflowResponse,
  RequestWorkflow,
  RequestWorkflowExternal,
  RequestWorkflowsData,
  RequestWorkflowWithNodes,
  SyncWorkflowFormResponse,
  SyncWorkflowRemoveFileResponse,
  SyncWorkflowUploadFileResponse,
  Workflow,
  WorkflowExternal,
  WorkflowFormErrors,
  WorkflowFormSubmission,
  WorkflowFormSubmissionExternal,
  WorkflowFormSubmissionFile,
  WorkflowFormValidationError,
  WorkflowInput,
  WorkflowNodeExternal,
  WorkflowNodeFormWithPopulatedFieldsExternal,
  WorkflowSchema,
  WorkflowFormSubmissionFileExternal,
  GetRequestMistakesResponse,
  RequestWorkflowCapabilities,
} from '@workflows/types';
import { RequestEntitiesService } from '../request';
import {
  distinctUntilChanged,
  map,
  Observable,
  of,
  skip,
  switchMap,
  tap,
} from 'rxjs';
import { RegistryCounterparty, RequestDTO } from '@core/types';
import { AccountService } from '@core/services/account';
import {
  WorkflowBackgroundSyncService,
  type WorkflowBackgroundSyncTask,
  WorkflowDataService,
  WorkflowErrorsService,
  WorkflowNavigationService,
  WorkflowSelectorService,
  WorkflowStateService,
} from './';

import {
  takeUntilDestroyed,
  toSignal,
  toObservable,
} from '@angular/core/rxjs-interop';
import { GetEventsOfRequestWorkflowResponse } from '@workflows/types/requestWorkflowEvents/getEventsOfRequestWorkflow';
import { FeatureFlagsService } from '@core/services/feature-flags.service';
import type { WorkflowFormDraftReadResult } from './workflow-data.types';
import {
  EMPTY_WORKFLOW_PENDING_WORK,
  WorkflowPendingWorkService,
} from './workflow-pending-work.service';
import { WorkflowOfflineService } from './workflow-offline.service';
import {
  computeWorkflowScreenState,
  isWorkflowDataForRequest,
} from './workflow-screen-state';
import { WorkflowFileFieldsSyncService } from './workflow-file-fields-sync.service';
import {
  WorkflowFileFieldChange,
  WorkflowFileFieldsSyncResult,
  WorkflowFileFieldTarget,
} from './workflow-file-field.types';

/**
 * Главный фасад для работы с workflow
 * Делегирует вызовы специализированным сервисам
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowService extends RequestEntitiesService<RequestWorkflowsData | null> {
  protected override readonly initialValue = null;

  // Инжекты специализированных сервисов
  private readonly dataService = inject(WorkflowDataService);
  private readonly selectorService = inject(WorkflowSelectorService);
  private readonly navigationService = inject(WorkflowNavigationService);
  private readonly stateService = inject(WorkflowStateService);
  private readonly errorsService = inject(WorkflowErrorsService);
  private readonly backgroundSyncService = inject(
    WorkflowBackgroundSyncService,
  );
  private readonly accountService = inject(AccountService);
  private readonly featureFlags = inject(FeatureFlagsService);

  // Публичный signal для доступа к данным
  public readonly $requestsWorkflowData =
    this.dataService.$requestsWorkflowData;

  /** Состояние последней загрузки данных регламентов (live / stale-offline / forbidden ...) */
  public readonly $loadState = this.dataService.$loadState;

  private readonly pendingWorkService = inject(WorkflowPendingWorkService);
  private readonly fileFieldsSyncService = inject(
    WorkflowFileFieldsSyncService,
  );
  private readonly workflowOfflineService = inject(WorkflowOfflineService);

  /** Неотправленная работа открытой заявки (формы, фото, завершение) */
  public readonly $pendingWork = toSignal(
    toObservable(this.requestService.$request).pipe(
      map(request => request?.requestNumber),
      distinctUntilChanged(),
      switchMap(requestNumber =>
        requestNumber
          ? this.pendingWorkService.watchRequest$(requestNumber)
          : of(EMPTY_WORKFLOW_PENDING_WORK),
      ),
    ),
    { initialValue: EMPTY_WORKFLOW_PENDING_WORK },
  );

  /** Единое состояние экрана регламентов (loading / live / forbidden / sync-pending ...) */
  public readonly $screenState = computed(() =>
    computeWorkflowScreenState(this.$loadState(), this.$pendingWork()),
  );

  /** Повторить отправку упавших задач открытой заявки */
  retryFailedSync = (): Promise<void> =>
    this.pendingWorkService.retryFailedForRequest(
      this.requestService.$request().requestNumber,
    );

  readonly source$ = (
    _requestId: number,
  ): Observable<RequestWorkflowsData | null> => {
    if (!this.featureFlags.workflowsEnabled) {
      this.dataService.$loadState.set('disabled');

      return of(null);
    }

    const { requestNumber } = this.requestService.$request();

    this.dataService.$loadState.set('loading');

    return this.dataService.loadWorkflowData(requestNumber).pipe(
      tap(result => this.dataService.$loadState.set(result.state)),
      map(result => result.data),
    );
  };

  constructor() {
    super();
    // Подписка на изменения данных для обновления сигнала
    // после того как появился или изменился requestId
    this.requestService.requestId$
      .pipe(
        // Данные прошлой заявки не должны оставаться на экране, пока грузится новая
        tap(() => this.dataService.$requestsWorkflowData.set(null)),
        switchMap(() => this.entities$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(wfData => {
        const requestNumber = this.requestService.$request().requestNumber;

        if (wfData && !isWorkflowDataForRequest(wfData, requestNumber)) {
          // Поздний/закэшированный результат другой заявки — не применяем
          return;
        }

        if (wfData) {
          this.dataService.updateWorkflowData(requestNumber, wfData);
        } else {
          // forbidden / not-found / нет данных: прежние данные не показываем
          this.dataService.$requestsWorkflowData.set(null);
        }
      });

    // Смена пользователя: данные прежнего пользователя сбрасываются и перезагружаются
    this.accountService.currentUser$
      .pipe(
        map(user => user?.id ?? null),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.dataService.$requestsWorkflowData.set(null);
        this.dataService.$loadState.set('idle');
        this.refreshEntities();
      });
  }

  // --------------------------------------------------------------------------
  // Делегирование к WorkflowSelectorService (выборка данных)
  // --------------------------------------------------------------------------

  getNodeForms = (nodeId: number) => this.selectorService.getNodeForms(nodeId);

  getFirstFormOfNode = (nodeId: number): number | null =>
    this.selectorService.getFirstFormOfNode(nodeId);

  getLastFormOfNode = (nodeId: number): number | null =>
    this.selectorService.getLastFormOfNode(nodeId);

  getFirstNodeOfWorkflow = (workflowId: string): WorkflowNodeExternal | null =>
    this.selectorService.getFirstNodeOfWorkflow(workflowId);

  getLastNodeOfWorkflow = (workflowId: string): WorkflowNodeExternal | null =>
    this.selectorService.getLastNodeOfWorkflow(workflowId);

  getNodeByOrder = (
    order: number,
    workflow: RequestWorkflowWithNodes | null,
  ): WorkflowNodeExternal | null =>
    this.selectorService.getNodeByOrder(order, workflow);

  getSortedNodes = (
    workflow: RequestWorkflowWithNodes | null,
  ): WorkflowNodeExternal[] => this.selectorService.getSortedNodes(workflow);

  getNodeIdByFormId(formId: number) {
    return this.selectorService.getNodeIdByFormId(formId);
  }

  getWorkFlowName = (requestWorkflowId: string): string =>
    this.selectorService.getWorkFlowName(requestWorkflowId);

  getWorkflowNodeForms = (
    workflowNodeId: number,
  ): Observable<WorkflowNodeFormWithPopulatedFieldsExternal[]> =>
    this.selectorService.getWorkflowNodeForms(workflowNodeId);

  isFirstForm = (requestWorkflowId: string, formID: string) =>
    this.selectorService.isFirstForm(requestWorkflowId, formID);

  isInputPhotosForm = (
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
  ) => this.selectorService.isInputPhotosForm(formId);

  getNodeValidFormsCount$ = (nodeId: number, requestWorkflowId: string) =>
    this.selectorService.getNodeValidFormsCount$(nodeId, requestWorkflowId);

  getWorkflowNodeFormsCount$ = (nodeId: number) =>
    this.selectorService.getWorkflowNodeFormsCount$(nodeId);

  allNodeFormsCompleted$ = (nodeId: number, requestWorkflowId: string) =>
    this.selectorService.allNodeFormsCompleted$(nodeId, requestWorkflowId);

  someNodeFormsAreInvalid$ = (nodeId: number, requestWorkflowId: string) =>
    this.selectorService.someNodeFormsAreInvalid$(nodeId, requestWorkflowId);

  nodeHasSubmissions$ = (nodeId: number, requestWorkflowId: string) =>
    this.selectorService.nodeHasSubmissions$(nodeId, requestWorkflowId);

  nodeIsInProgress$ = (nodeId: number) =>
    this.selectorService.nodeIsInProgress$(nodeId);

  formIsInvalid$ = (formId: number, requestWorkflowId: string) =>
    this.selectorService.formIsInvalid$(formId, requestWorkflowId);

  formIsSubmitted$ = (formId: number, requestWorkflowId: string) =>
    this.selectorService.formIsSubmitted$(formId, requestWorkflowId);

  allWorkflowNodesCompleted$ = (requestWorkflowId: string) =>
    this.selectorService.allWorkflowNodesCompleted$(requestWorkflowId);

  areWorkflowsCompleted = () => this.selectorService.areWorkflowsCompleted();

  areAllWorkflowNodesCompleted = () =>
    this.selectorService.areAllWorkflowNodesCompleted();

  requestWorkflowsExist = this.selectorService.requestWorkflowsExist;

  /**
   * Права берутся из capabilities ответа workflows/data (backend проверяет то же самое
   * на каждом действии). Нет capabilities — действие недоступно.
   */
  canExecuteWorkflow = (workflowId: RequestWorkflowExternal['id']): boolean =>
    this.getRequestWorkflowCapabilities(workflowId)?.canExecute === true;

  /** Ошибки заявки может отмечать тот, кто выполняет этот регламент */
  canSetRequestMistakes = (
    workflowId: RequestWorkflowExternal['id'],
  ): boolean =>
    this.getRequestWorkflowCapabilities(workflowId)?.canSetMistakes === true;

  canCompleteRequestFromWorkflows = (): boolean =>
    this.$requestsWorkflowData()?.capabilities?.canComplete === true;

  private getRequestWorkflowCapabilities(
    workflowId: RequestWorkflowExternal['id'],
  ): RequestWorkflowCapabilities | undefined {
    return this.$requestsWorkflowData()?.requestsWorkflows.find(
      workflow => workflow.id === workflowId,
    )?.capabilities;
  }

  // --------------------------------------------------------------------------
  // Делегирование к WorkflowNavigationService (навигация)
  // --------------------------------------------------------------------------

  getStartWorkflowUrl$ = () => this.navigationService.getStartWorkflowUrl$();

  getNextWorkflow = (id: string) => this.selectorService.getNextWorkflow(id);

  getPreviousWorkflow = (id: string) =>
    this.selectorService.getPreviousWorkflow(id);

  getLastWorkflow = () => this.selectorService.getLastWorkflow();

  getNextFormInNode = (nodeId: number, currentFormId: number) =>
    this.selectorService.getNextFormInNode(nodeId, currentFormId);

  getNextFormInWorkflow = (
    workflowId: string,
    currentNodeId: number,
    currentFormId: number,
  ) =>
    this.selectorService.getNextFormInWorkflow(
      workflowId,
      currentNodeId,
      currentFormId,
    );

  getNextRouteAfterSync = (
    workflowId: string,
    currentNodeId: number,
    currentFormId: number,
    baseRoute: string,
  ) =>
    this.navigationService.getNextRouteAfterSync(
      workflowId,
      currentNodeId,
      currentFormId,
      baseRoute,
    );

  getReturnLink = (
    workflowId: string,
    nodeId: number,
    formId: number,
    baseRoute: string,
    isFinalPage: boolean,
  ) =>
    this.navigationService.getReturnLink(
      workflowId,
      nodeId,
      formId,
      baseRoute,
      isFinalPage,
    );

  // --------------------------------------------------------------------------
  // Делегирование к WorkflowStateService (мутации и API)
  // --------------------------------------------------------------------------

  updateFormSubmission = (
    workflowId: string,
    nodeID: number,
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    submissionData?: WorkflowFormSubmissionExternal['data'],
    isValid?: boolean,
    updateLastVisitedFormId = true,
  ) =>
    this.stateService.updateFormSubmission(
      workflowId,
      nodeID,
      formId,
      submissionData,
      isValid,
      updateLastVisitedFormId,
    );

  getSubmission = (requestWorkflowId: string, formId: number) =>
    this.stateService.getSubmission(requestWorkflowId, formId);

  getFilesFromSubmission = (submissionId: WorkflowFormSubmission['id']) =>
    this.stateService.getFilesFromSubmission(submissionId);

  removeSubmissionFile = (
    requestWorkflowId: string,
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    submissionFileId: WorkflowFormSubmissionFile['id'],
  ): void =>
    this.stateService.removeSubmissionFile(
      requestWorkflowId,
      formId,
      submissionFileId,
    );

  syncWorkflow = (
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    formData: object,
    operationId?: string,
  ): Observable<SyncWorkflowFormResponse> =>
    this.stateService.syncWorkflow(
      requestNumber,
      requestWorkflowId,
      nodeId,
      formId,
      formData,
      operationId,
    );

  syncWorkflowUploadFile = (
    requestNumber: RequestDTO['requestNumber'],
    workflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    fieldId: string,
    formData: FormData,
    operationId?: string,
  ): Observable<SyncWorkflowUploadFileResponse> =>
    this.stateService.syncWorkflowUploadFile(
      requestNumber,
      workflowId,
      nodeId,
      formId,
      fieldId,
      formData,
      operationId,
    );

  syncWorkflowRemoveFile = (
    requestNumber: RequestDTO['requestNumber'],
    workflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    fieldId: WorkflowSchema,
    submissionFileId: WorkflowFormSubmissionFile['id'],
    operationId?: string,
  ): Observable<SyncWorkflowRemoveFileResponse> =>
    this.stateService.syncWorkflowRemoveFile(
      requestNumber,
      workflowId,
      nodeId,
      formId,
      fieldId,
      submissionFileId,
      operationId,
    );

  completeRequestWorkflows = (
    requestNumber: RequestDTO['requestNumber'],
    operationId?: string,
  ) => this.stateService.completeRequestWorkflows(requestNumber, operationId);

  // --------------------------------------------------------------------------
  // Делегирование к WorkflowBackgroundSyncService (фоновые сабмиты)
  // --------------------------------------------------------------------------

  syncFormInBackground = (task: WorkflowBackgroundSyncTask): void =>
    this.backgroundSyncService.syncFormInBackground(task);

  /**
   * Сохранить форму без сети: задача записывается на устройство до перехода дальше по мастеру.
   * Ошибка записи пробрасывается — мастер должен остаться на форме.
   */
  queueFormOffline = (task: WorkflowBackgroundSyncTask): Promise<void> =>
    this.backgroundSyncService.queueFormOffline(task);

  getBackgroundSyncPendingCountForNode$ = (nodeId: number) =>
    this.backgroundSyncService.getPendingCountForNode$(nodeId);

  // --------------------------------------------------------------------------
  // Делегирование к WorkflowFileFieldsSyncService (поля inputFiles)
  // --------------------------------------------------------------------------

  /** Добавить загруженный файл в submission экземпляра формы */
  addSubmissionFile = (
    requestWorkflowId: string,
    nodeId: number,
    formId: number,
    submissionFile: WorkflowFormSubmissionFileExternal,
  ): void =>
    this.stateService.addSubmissionFile(
      requestWorkflowId,
      nodeId,
      formId,
      submissionFile,
    );

  /** Файлы поля: серверные и ожидающие отправки из очереди на устройстве */
  loadFileFieldValue = (
    target: WorkflowFileFieldTarget,
    fieldKey: string,
    submission: WorkflowFormSubmissionExternal | null,
  ): Promise<File[]> =>
    this.fileFieldsSyncService.loadFieldFiles(target, fieldKey, submission);

  /**
   * Загрузить новые и удалить убранные файлы полей до сохранения формы.
   * Без сети операции записываются в очередь на устройстве.
   */
  syncFileFieldChanges = (
    target: WorkflowFileFieldTarget,
    changes: readonly WorkflowFileFieldChange[],
  ): Promise<WorkflowFileFieldsSyncResult> =>
    this.fileFieldsSyncService.syncFieldChanges(target, changes);

  // --------------------------------------------------------------------------
  // Делегирование к WorkflowDataService (IndexedDB, драфты форм)
  // --------------------------------------------------------------------------

  clearIndexedDBForRequest = (requestNumber: RequestDTO['requestNumber']) =>
    this.dataService.clearIndexedDBForRequest(requestNumber);

  clearWorkflowDataForRequest = (requestNumber: RequestDTO['requestNumber']) =>
    this.dataService.clearWorkflowDataForRequest(requestNumber);

  /**
   * Сохранить драфт формы
   */
  saveFormDraft = (
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
    formValue: Record<string, unknown>,
  ): Promise<void> =>
    this.dataService.saveFormDraft(
      requestNumber,
      workflowId,
      nodeId,
      formId,
      formValue,
    );

  /**
   * Получить драфт формы
   */
  getFormDraft = (
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
  ): Promise<WorkflowFormDraftReadResult> =>
    this.dataService.getFormDraft(requestNumber, workflowId, nodeId, formId);

  /**
   * Удалить драфт формы
   */
  removeFormDraft = (
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
  ): Promise<void> =>
    this.dataService.removeFormDraft(requestNumber, workflowId, nodeId, formId);

  // --------------------------------------------------------------------------
  // Debug методы
  // --------------------------------------------------------------------------

  getAllWorkflows = (): Observable<Workflow[]> =>
    this.stateService.getAllWorkflows();

  removeWorkflow(
    workflowId: WorkflowExternal['id'],
  ): Observable<DeleteWorkflowResponse> {
    return this.stateService.removeWorkflow(workflowId);
  }

  getAllRequestWorkflowsOfRequest = (
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<RequestWorkflow[]> =>
    this.stateService.getAllRequestWorkflowsOfRequest(requestNumber);

  attachSelectedWorkflows = (
    requestNumber: RequestDTO['requestNumber'],
    selectedWorkflowIds: string[],
  ): Observable<{ message: string }> =>
    this.stateService.attachSelectedWorkflows(
      requestNumber,
      selectedWorkflowIds,
    );

  detachAllWorkflows = (
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<{ message: string }> =>
    this.stateService.detachAllWorkflows(requestNumber);

  clearAllWorkflowsDataForRequest = (
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<{ message: string }> =>
    this.stateService.clearAllWorkflowsDataForRequest(requestNumber);

  importWorkflow = (
    workflowJson: WorkflowInput,
    publish = false,
  ): Observable<CreateWorkflowSuccessResponse> =>
    this.stateService.importWorkflow(workflowJson, publish);

  // --------------------------------------------------------------------------
  // Делегирование к WorkflowErrorsService (ошибки валидации)
  // --------------------------------------------------------------------------

  /**
   * Сохранить ошибки валидации формы
   */
  saveFormErrors = (
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
    errors: WorkflowFormValidationError[],
  ): Promise<void> =>
    this.errorsService.saveFormErrors(
      requestNumber,
      workflowId,
      nodeId,
      formId,
      errors,
    );

  /**
   * Получить ошибки валидации формы
   */
  getFormErrors = (
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
  ): Promise<WorkflowFormErrors | null> =>
    this.errorsService.getFormErrors(requestNumber, workflowId, nodeId, formId);

  /**
   * Удалить ошибки валидации формы
   */
  removeFormErrors = (
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
  ): Promise<void> =>
    this.errorsService.removeFormErrors(
      requestNumber,
      workflowId,
      nodeId,
      formId,
    );

  /**
   * Очистить все ошибки валидации для заявки
   */
  clearAllFormErrors = (requestNumber: string): Promise<void> =>
    this.errorsService.clearAllFormErrors(requestNumber);

  /**
   * Получить все ошибки для заявки
   */
  getAllFormErrorsForRequest = (
    requestNumber: string,
  ): Promise<WorkflowFormErrors[]> =>
    this.errorsService.getAllFormErrorsForRequest(requestNumber);

  /**
   * Применить ошибки валидации к контролам формы
   */
  applyErrorsToForm = (
    errors: WorkflowFormValidationError[],
    formControls: { [key: string]: any },
  ): void => this.errorsService.applyErrorsToForm(errors, formControls);

  /**
   * Построить массив валидаторов на основе схемы валидации
   */
  buildValidators = (validation: any, fieldType?: string) =>
    this.errorsService.buildValidators(validation, fieldType);

  /**
   * Парсит fieldUrl из ответа сервера
   * Формат: workflow://workflowId/node_nodeId/form_formId/fieldKey
   */
  parseFieldUrl = (fieldUrl: string) =>
    this.errorsService.parseFieldUrl(fieldUrl);

  /**
   * Маппинг правила валидации в человекочитаемое сообщение
   */
  getValidationErrorMessage = (validationRule: string, ruleValue?: any) =>
    this.errorsService.getValidationErrorMessage(validationRule, ruleValue);

  public setRequestMistakes(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
    mistakes: { mistakeTypeId: number; comment: string }[],
  ) {
    return this.dataService.setRequestMistakes(
      requestNumber,
      requestWorkflowId,
      mistakes,
    );
  }

  /** Текущие ошибки заявки (предзаполнение диалога ошибок) */
  public getRequestMistakes(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
  ): Observable<GetRequestMistakesResponse> {
    return this.dataService.getRequestMistakes(
      requestNumber,
      requestWorkflowId,
    );
  }

  public getMistakesTypes(
    counterparty: RegistryCounterparty['id'],
  ): Observable<any> {
    return this.dataService.getMistakesTypes(counterparty);
  }

  // Получить события регламента заявки
  public getEventsOfRequestWorkflow(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
  ): Observable<GetEventsOfRequestWorkflowResponse> {
    return this.dataService.getEventsOfRequestWorkflow(
      requestNumber,
      requestWorkflowId,
    );
  }

  // Получить события всех регламентов заявки
  public getEventsOfAllRequestWorkflows(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<GetEventsOfRequestWorkflowResponse> {
    return this.dataService.getEventsOfAllRequestWorkflows(requestNumber);
  }
}
